import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useUserId } from '@client/hooks/business/user/useUserId';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Skeleton, Switch, Tooltip } from 'antd';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/user-settings/notifications')({
    staticData: {
        metadata: {
            title: 'Notifications',
        },
        ui: {
            widthVariant: 'compact',
        },
    },
    component: NotificationSettings,
});

type UserNotificationInput = RouterInputs['user']['updateUserNotifications']['set'];
type NotificationField = keyof UserNotificationInput;

function NotificationSettings() {
    const userId = useUserId();
    const { data, isPending } = trpc.user.getUserNotifications.useQuery(userId ? { userId } : skipToken);
    const { mutate } = trpc.user.updateUserNotifications.useMutation();

    const [settings, setSettings] = useState<UserNotificationInput | null>(null);
    const [savingField, setSavingField] = useState<NotificationField | null>(null);

    useEffect(() => {
        if (data) {
            setSettings(data);
        }
    }, [data]);

    const handleToggle = (field: NotificationField, checked: boolean, linkedField?: NotificationField) => {
        if (!settings) return;

        const updates: Partial<UserNotificationInput> = { [field]: checked };

        // Handle mutually exclusive fields
        if (checked && linkedField) {
            updates[linkedField] = false;
        }

        const newSettings = { ...settings, ...updates };
        setSettings(newSettings);
        setSavingField(field);

        mutate(
            { userId, set: newSettings },
            {
                onSuccess() {
                    message.success('Saved');
                    setSavingField(null);
                },
                onError() {
                    // Revert on error
                    setSettings(settings);
                    setSavingField(null);
                    message.error('Failed to save');
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <Card>
                {isPending || !settings ? (
                    <Skeleton active />
                ) : (
                    <>
                        <div className="mb-6">
                            <h3 className="mb-1 font-semibold text-foreground text-lg">Notification preferences</h3>
                            <p className="text-muted-foreground text-sm">
                                Configure which email notifications you receive for different events.
                            </p>
                        </div>

                        <Divider titlePlacement="start" className="mt-0 mb-2">
                            Tasks
                        </Divider>
                        <NotificationRow
                            title="Assigned to task"
                            description="When a task is assigned to me"
                            checked={settings.notificationAssignedToTask ?? false}
                            loading={savingField === 'notificationAssignedToTask'}
                            onChange={(checked) => handleToggle('notificationAssignedToTask', checked)}
                        />
                        <NotificationRow
                            title="New unallocated task"
                            description="When a new task is created in my organisation but hasn't been allocated to anyone"
                            checked={settings.notificationNewUnallocatedTask ?? false}
                            loading={savingField === 'notificationNewUnallocatedTask'}
                            onChange={(checked) => handleToggle('notificationNewUnallocatedTask', checked)}
                        />

                        <Divider titlePlacement="start" className="mt-10 mb-2">
                            Complaints
                        </Divider>
                        <NotificationRow
                            title="Internal complaint submitted"
                            description="When an internal complaint is submitted to my handling party"
                            checked={settings.notificationComplaintCreated ?? false}
                            loading={savingField === 'notificationComplaintCreated'}
                            onChange={(checked) => handleToggle('notificationComplaintCreated', checked)}
                        />
                        <NotificationRow
                            title="Public complaint submitted"
                            description="When a new public complaint is submitted to my organisation"
                            checked={settings.notificationComplaintPublicSubmitted ?? false}
                            loading={savingField === 'notificationComplaintPublicSubmitted'}
                            onChange={(checked) => handleToggle('notificationComplaintPublicSubmitted', checked)}
                        />
                        <NotificationRow
                            title="Complaint created for my handling party"
                            description="When an internal complaint is assigned to my handling party"
                            checked={settings.notificationComplaintCreatedInMyExternalOrg ?? false}
                            loading={savingField === 'notificationComplaintCreatedInMyExternalOrg'}
                            onChange={(checked) => handleToggle('notificationComplaintCreatedInMyExternalOrg', checked)}
                        />
                        <NotificationRow
                            title="Assigned as owner"
                            description="When I am assigned as owner of a complaint"
                            checked={settings.notificationAssignedToComplaintAsOwner ?? false}
                            loading={savingField === 'notificationAssignedToComplaintAsOwner'}
                            onChange={(checked) => handleToggle('notificationAssignedToComplaintAsOwner', checked)}
                        />
                        <NotificationRow
                            title="Complaint indicator changed for complaints in my handling party"
                            description="When the traffic light status changes for a complaint in my handling party"
                            checked={settings.notificationComplaintIndicatorChangedInMyHandlingParty ?? false}
                            loading={savingField === 'notificationComplaintIndicatorChangedInMyHandlingParty'}
                            onChange={(checked) =>
                                handleToggle(
                                    'notificationComplaintIndicatorChangedInMyHandlingParty',
                                    checked,
                                    'notificationComplaintIndicatorChanged',
                                )
                            }
                            tooltip="Only one of these notifications can be enabled to avoid duplicate emails for the same item"
                        />
                        <NotificationRow
                            title="Complaint indicator changed for my complaints"
                            description="When the traffic light status changes for one of my complaints"
                            checked={settings.notificationComplaintIndicatorChanged ?? false}
                            loading={savingField === 'notificationComplaintIndicatorChanged'}
                            onChange={(checked) =>
                                handleToggle(
                                    'notificationComplaintIndicatorChanged',
                                    checked,
                                    'notificationComplaintIndicatorChangedInMyHandlingParty',
                                )
                            }
                            tooltip="Only one of these notifications can be enabled to avoid duplicate emails for the same item"
                        />

                        <Divider titlePlacement="start" className="mt-10 mb-2">
                            Incidents
                        </Divider>
                        <NotificationRow
                            title="Incident submitted"
                            description="When an incident is submitted to my handling party"
                            checked={settings.notificationIncidentSubmitted ?? false}
                            loading={savingField === 'notificationIncidentSubmitted'}
                            onChange={(checked) => handleToggle('notificationIncidentSubmitted', checked)}
                        />
                        <NotificationRow
                            title="Assigned as owner"
                            description="When I am assigned as owner of an incident"
                            checked={settings.notificationIncidentAssignedAsOwner ?? false}
                            loading={savingField === 'notificationIncidentAssignedAsOwner'}
                            onChange={(checked) => handleToggle('notificationIncidentAssignedAsOwner', checked)}
                        />
                        <NotificationRow
                            title="Incident indicator changed for incidents in my handling party"
                            description="When the traffic light status changes for an incident in my handling party"
                            checked={settings.notificationIncidentIndicatorChangedInMyHandlingParty ?? false}
                            loading={savingField === 'notificationIncidentIndicatorChangedInMyHandlingParty'}
                            onChange={(checked) =>
                                handleToggle(
                                    'notificationIncidentIndicatorChangedInMyHandlingParty',
                                    checked,
                                    'notificationIncidentIndicatorChanged',
                                )
                            }
                            tooltip="Only one of these notifications can be enabled to avoid duplicate emails for the same item"
                        />
                        <NotificationRow
                            title="Incident indicator changed for my incidents"
                            description="When the traffic light status changes for one of my incidents"
                            checked={settings.notificationIncidentIndicatorChanged ?? false}
                            loading={savingField === 'notificationIncidentIndicatorChanged'}
                            onChange={(checked) =>
                                handleToggle(
                                    'notificationIncidentIndicatorChanged',
                                    checked,
                                    'notificationIncidentIndicatorChangedInMyHandlingParty',
                                )
                            }
                            tooltip="Only one of these notifications can be enabled to avoid duplicate emails for the same item"
                        />

                        <Divider titlePlacement="start" className="mt-10 mb-2">
                            Controls
                        </Divider>
                        <NotificationRow
                            title="Assigned to control activity"
                            description="When I am assigned as owner of a control activity"
                            checked={settings.notificationAssignedToControlActivity ?? false}
                            loading={savingField === 'notificationAssignedToControlActivity'}
                            onChange={(checked) => handleToggle('notificationAssignedToControlActivity', checked)}
                        />
                        <NotificationRow
                            title="Assigned to control test"
                            description="When I am assigned as tester of a control test"
                            checked={settings.notificationAssignedToControlTest ?? false}
                            loading={savingField === 'notificationAssignedToControlTest'}
                            onChange={(checked) => handleToggle('notificationAssignedToControlTest', checked)}
                        />

                        <Divider titlePlacement="start" className="mt-10 mb-2">
                            Material service provider
                        </Divider>
                        <NotificationRow
                            title="Assigned to governance check"
                            description="When I am assigned as owner of an MSPR governance check"
                            checked={settings.notificationAssignedToGovernanceCheck ?? false}
                            loading={savingField === 'notificationAssignedToGovernanceCheck'}
                            onChange={(checked) => handleToggle('notificationAssignedToGovernanceCheck', checked)}
                        />

                        <Divider titlePlacement="start" className="mt-10 mb-2">
                            Risk
                        </Divider>
                        <NotificationRow
                            title="Assigned to risk action"
                            description="When I am assigned as owner of a risk action plan"
                            checked={settings.notificationAssignedToRiskAction ?? false}
                            loading={savingField === 'notificationAssignedToRiskAction'}
                            onChange={(checked) => handleToggle('notificationAssignedToRiskAction', checked)}
                        />
                    </>
                )}
            </Card>
        </PageController>
    );
}

type NotificationRowProps = {
    title: string;
    description: string;
    checked: boolean;
    loading: boolean;
    onChange: (checked: boolean) => void;
    tooltip?: string;
};

function NotificationRow({ title, description, checked, loading, onChange, tooltip }: NotificationRowProps) {
    return (
        <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex-1">
                <div className="flex items-center gap-2 font-medium text-foreground">
                    {title}
                    {tooltip && (
                        <Tooltip title={tooltip}>
                            <Info className="h-4 w-4 text-muted-foreground" />
                        </Tooltip>
                    )}
                </div>
                <div className="text-muted-foreground text-sm">{description}</div>
            </div>
            <Switch checked={checked} loading={loading} onChange={onChange} />
        </div>
    );
}
